import type { IUser } from '@/shared/api/types/user-types'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUsersQuery } from '@/entities/user'
import { UserFormDrawer } from '@/features/user/user-form-drawer'
import { UserListTable } from '@/features/user/user-list-table'
import { UserSearchBar } from '@/features/user/user-search-bar'
import { DEFAULT_PAGE_SIZE } from '@/shared/constants/pagination'
import { useScopedAdminPath } from '@/shared/hooks/use-scoped-admin-path'
import { Button } from '@/shared/ui/button'
import { DataTableFilterField, DataTableToolbar } from '@/shared/ui/data-table'
import { PageContainer } from '@/shared/ui/page-container'
import { PageHeader } from '@/shared/ui/page-header'

export default function UserListPage() {
  const { t } = useTranslation()
  const scopedAdminPath = useScopedAdminPath()
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<IUser | null>(null)

  const skip = (page - 1) * pageSize
  const { data, isLoading } = useUsersQuery({
    skip,
    limit: pageSize,
    ...(searchTerm.trim() ? { search: searchTerm.trim() } : {}),
  })

  const users = data?.items ?? []
  const total = data?.total ?? 0

  function handleSearch(term: string) {
    setSearchTerm(term)
    setPage(1)
  }

  function handlePageChange(newPage: number, newPageSize: number) {
    setPage(newPage)
    setPageSize(newPageSize)
  }

  function handleCreate() {
    setEditingUser(null)
    setDrawerOpen(true)
  }

  function handleEdit(user: IUser) {
    setEditingUser(user)
    setDrawerOpen(true)
  }

  function handleDrawerClose() {
    setDrawerOpen(false)
    setEditingUser(null)
  }

  return (
    <PageContainer>
      <PageHeader
        title={t('user.title')}
        breadcrumbs={[
          { label: t('sidebar.dashboard'), path: scopedAdminPath('/dashboard') },
          { label: t('sidebar.users') },
        ]}
        actions={
          <Button onClick={handleCreate}>
            <Plus className="mr-1.5 h-4 w-4" />
            {t('user.create')}
          </Button>
        }
      />

      <DataTableToolbar>
        <DataTableFilterField
          label={t('common.search', { defaultValue: 'Search' })}
          className="min-w-[280px] w-full max-w-xl"
        >
          <UserSearchBar onSearch={handleSearch} className="w-full" />
        </DataTableFilterField>
      </DataTableToolbar>

      <UserListTable
        users={users}
        loading={isLoading}
        pagination={{ current: page, total, pageSize }}
        onPageChange={handlePageChange}
        onEdit={handleEdit}
      />

      <UserFormDrawer open={drawerOpen} user={editingUser} onClose={handleDrawerClose} />
    </PageContainer>
  )
}
